import React, { useState, useEffect, useRef } from 'react'
import { AiOutlineMenu } from "react-icons/ai";
import { useNavigate, NavLink } from "react-router-dom";
import BaseUrl from "../BaseUrl";
import Logo from '../Assets/Finel-logo-PNG.png'
import useOnClickOutside from "./use-onclick-outside";
import { useProfile } from '../context/ProfileContext';
import ConfirmDialog from './ConfirmDialog';
import {
    IconHome,
    IconProfile,
    IconReports,
    IconHelpCenter,
    IconRatingSystem,
    IconLogout,
} from './NavIcons';

const AddGoalIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5h14v14H5z" />
    </svg>
);

const GoalListIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h10M9 12h10M9 17h10M5 7h.01M5 12h.01M5 17h.01" />
    </svg>
);

const UserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 14a4 4 0 10-8 0m8 0c0 2.21-1.79 4-4 4s-4-1.79-4-4m8 0h3m-3 0H8m11-4v6" />
    </svg>
);

const PlanIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-2.21 0-4 1.343-4 3s1.79 3 4 3 4 1.343 4 3-1.79 3-4 3m0-12V6m0 14v-2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v14H4z" />
    </svg>
);

const UserListIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m8-6a3 3 0 11-6 0 3 3 0 016 0zM9 8a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

const navItemClass = ({ isActive }) =>
    [
        'group flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-sm font-semibold transition-all duration-200',
        isActive
            ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-md shadow-sky-500/20'
            : 'text-slate-600 hover:bg-sky-50 hover:text-sky-700',
    ].join(' ');

export default function Navbar() {
    const navigate = useNavigate()
    const { profile, fetchProfile } = useProfile()
    const [sideBar, setSideBar] = useState(false);
    const [logoutOpen, setLogoutOpen] = useState(false);
    const ref = useRef();

    useOnClickOutside(ref, () => setSideBar(false));

    const performLogout = () => {
        localStorage.clear();
        navigate("/login");
    };

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const storedProfile = JSON.parse(localStorage.getItem('profile') || '{}');
    const isCompany = (profile?.role || storedProfile.role) === 'company';
    const displayName = profile?.fullName || storedProfile.fullName || 'My Profile';
    const profileImage = profile?.profile
        ? `${BaseUrl()}${profile.profile}`
        : undefined;

    const navItems = [
        { label: 'Dashboard', to: '/home', icon: <IconHome /> },
        { label: 'Profile', to: '/my-profile', icon: <IconProfile /> },
        { label: 'Add Goal', to: '/add-goal', icon: <AddGoalIcon /> },
        { label: 'Goal List', to: '/goal-list', icon: <GoalListIcon /> },
        ...(isCompany
            ? [
                { label: 'Add User', to: '/add-user', icon: <UserIcon /> },
                { label: 'My Plan', to: '/my-plan', icon: <PlanIcon /> },
                { label: 'User List', to: '/user-list', icon: <UserListIcon /> },
            ]
            : []),
        { label: 'My Reports', to: '/my-report', icon: <IconReports /> },
        { label: 'Help Center', to: '/help-center', icon: <IconHelpCenter /> },
        { label: 'Rating System', to: '/google-earth', icon: <IconRatingSystem /> },
    ];

    return (
        <>
            <header className="sticky top-0 z-30 w-full border-b border-white/70 bg-white/85 px-3 py-2.5 shadow-sm shadow-sky-900/5 backdrop-blur-xl sm:px-5 lg:px-6">
                <div className="flex w-full min-w-0 items-center gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setSideBar(true)}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-sky-100 bg-white text-sky-700 shadow-sm transition hover:-translate-y-0.5 hover:border-sky-200 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-300 md:hidden"
                            aria-label="Open menu"
                        >
                            <AiOutlineMenu className="h-5 w-5" />
                        </button>
                        <div className="min-w-0">
                            <p className="truncate text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-600">Dashboard</p>
                            <h1 className="truncate text-base font-bold text-slate-900 sm:text-lg">Welcome back</h1>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate('/my-profile')}
                        className="ml-auto flex shrink-0 items-center justify-end gap-2.5 rounded-2xl border border-sky-100 bg-white px-2.5 py-1.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-300"
                    >
                        <div className="h-10 w-10 overflow-hidden rounded-2xl bg-gradient-to-br from-sky-100 to-blue-100 ring-2 ring-white">
                            {profileImage ? (
                                <img
                                    className="h-full w-full object-cover"
                                    alt={displayName}
                                    src={profileImage}
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-sm font-bold text-sky-700">
                                    {displayName.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="hidden sm:block">
                            <p className="text-sm font-bold leading-5 text-slate-900">{displayName}</p>
                            <p className="text-[11px] font-medium text-slate-500">View profile</p>
                        </div>
                    </button>
                </div>
            </header>

            <ConfirmDialog
                open={logoutOpen}
                title="Log out?"
                message="Are you sure you want to log out?"
                confirmLabel="Yes"
                cancelLabel="No"
                onConfirm={() => {
                    setLogoutOpen(false);
                    performLogout();
                }}
                onCancel={() => setLogoutOpen(false)}
            />

            {sideBar && (
                <div className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-sm md:hidden">
                    <aside
                        ref={ref}
                        className="flex h-full w-72 max-w-[84vw] flex-col border-r border-sky-100 bg-white px-4 py-5 shadow-2xl"
                    >
                        <div className="mb-5 flex items-center justify-between">
                            <img src={Logo} className="h-10 w-auto object-contain" alt="Serendipity" />
                            <button
                                type="button"
                                onClick={() => setSideBar(false)}
                                className="rounded-xl px-3 py-2 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                            >
                                Close
                            </button>
                        </div>

                        <nav className="flex-1 space-y-1.5 overflow-y-auto">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    onClick={() => setSideBar(false)}
                                    className={navItemClass}
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/10 text-current ring-1 ring-slate-200/60 [&>svg]:h-5 [&>svg]:w-5">
                                        {item.icon}
                                    </span>
                                    <span>{item.label}</span>
                                </NavLink>
                            ))}
                        </nav>

                        <button
                            type="button"
                            onClick={() => setLogoutOpen(true)}
                            className="mt-3 flex w-full items-center gap-2.5 rounded-2xl px-3 py-2.5 text-left text-sm font-semibold text-slate-600 transition hover:bg-red-50 hover:text-red-600"
                        >
                            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-50 ring-1 ring-slate-200 [&>svg]:h-5 [&>svg]:w-5">
                                <IconLogout />
                            </span>
                            <span>Logout</span>
                        </button>
                    </aside>
                </div>
            )}
        </>
    )
}
